import {
  TabContent,
  Row,
  Column,
  FlexBox,
  Typography,
  RatingsStars,
  Link,
  Box,
  AlertBox,
  useScreenClass,
  FluidImage
} from '@vp/swan'
import { useMemo } from 'react'

import StockOptionCTA from './StockOptionCTA'
import { useAppContext } from '../../state/AppContext'
import { CouponCallout } from '@vp/coupon-callout'
import { stylesheet } from './StockOptionSection.scss'
import { PriceDisplay } from '@vp/price-platform'
import { trackNavigationClicks } from '../../utils/tracking/tracking'
import { useStyles } from '@vp/ubik-context'
import BulletPointsDescription from '../BulletPointsDescription'
import { CalloutWrapper } from '../CalloutWrapper'
import { StockOptionItem } from './types'
import { buildProductCallout } from '../../utils/callouts/productCalloutBuilder'
import { transformStockOptionHeroImage } from '../AutoGenerate/cloudinaryImageTransformer'
import { DISABLED_STOCK_OPTIONS_UPLOAD_FLOW_MPV_IDS } from './constants'

export interface StockOptionContentProps {
  mpvId: string
  item: StockOptionItem
  browseDesign: string
  browseDesignCta: string
  uploadDesignCta: string
  viewDetailsCta: string
  highlightsSectionTitle: string
  tabIndex: number
  outOfStockContent: string
  isActiveTab?: boolean
  hasBeenVisited?: boolean
  currentTabId: string
}

const StockOptionContent = (props: StockOptionContentProps) => {
  const {
    mpvId,
    item,
    browseDesignCta,
    uploadDesignCta,
    viewDetailsCta,
    browseDesign,
    highlightsSectionTitle,
    tabIndex,
    outOfStockContent,
    currentTabId,
    isActiveTab,
    hasBeenVisited
  } = props

  const { merchandisingCategoryId, dictionaryEntries } = useAppContext()
  const isMobile = useScreenClass() === 'xs'
  useStyles(stylesheet)

  const highlights = useMemo(() => {
    if (
      !item.description ||
      typeof item.description !== 'string' ||
      item.description.trim() === ''
    ) {
      return []
    }

    return item.description
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
  }, [item.description])

  const calloutDetails = useMemo(() => {
    return buildProductCallout({
      isOutOfStock: item?.isOutOfStock ?? false,
      outOfStockText: dictionaryEntries?.['outOfStock'] || '',
      callout: item?.callout
    })
  }, [item?.isOutOfStock, item?.callout, dictionaryEntries])

  const isUploadFlowDisabled = (mpvId: string) => {
    return DISABLED_STOCK_OPTIONS_UPLOAD_FLOW_MPV_IDS.includes(mpvId)
  }

  return (
    (isActiveTab || hasBeenVisited) && (
      <>
        <TabContent key={mpvId} tabId={currentTabId}>
          <Row>
            {item.isOutOfStock && (
              <Column span={12} marginBottom={6}>
                <AlertBox skin="warning" data-testid="outOfStockAlert">
                  {outOfStockContent}
                </AlertBox>
              </Column>
            )}
            <Column span={6} marginBottom={{ xs: 7, sm: 7, md: 8 }}>
              <Box>
                <FluidImage
                  src={transformStockOptionHeroImage(item.image.src)}
                  alt=""
                  loading="lazy"
                  data-testid="heroImage"
                  className="hero-image-border"
                />
              </Box>
            </Column>
            <Column span={6}>
              <Row>
                <Column span={12} marginBottom={6}>
                  <FlexBox
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap={isMobile ? 'wrap' : 'unset'}
                    gap={1}
                  >
                    <Typography fontSkin="title-section" marginBottom={2} data-testid="title">
                      {item.name}
                      {item.review && (
                        <RatingsStars
                          data-testid="ratingsStars"
                          style={{
                            display: !isMobile ? 'inline-block' : 'unset'
                          }}
                          ratingValue={item.review.averageRating}
                          reviews={item.review.reviewsCount}
                          fontSkin="body-standard"
                          marginLeft={3}
                        />
                      )}
                    </Typography>
                    <Link
                      data-testid="viewDetailsLink"
                      marginBottom={{ xs: 3 }}
                      href={item.linkUrl}
                      fontSkin="body-standard"
                      covering
                      onClick={() => {
                        trackNavigationClicks({
                          categoryId: merchandisingCategoryId,
                          pageZone: 'stockOptions',
                          destinationUrl: item.linkUrl,
                          tabIndex: tabIndex + 1,
                          stockOptionsCtaText: 'View full details header'
                        })
                      }}
                    />
                  </FlexBox>
                  {calloutDetails && (
                    <CalloutWrapper
                      callout={calloutDetails}
                      mpvId={mpvId}
                      enableCampaignCallouts
                      styleConfig={{ marginBottom: 2 }}
                    />
                  )}
                  <Typography fontSkin="body-standard" data-testid="productDescription">
                    {item.defaultProductDescription}
                  </Typography>
                </Column>
              </Row>
              <Row>
                {highlights && highlights.length > 0 ? (
                  <Column span={12} spanXs={12}>
                    <Typography
                      fontSkin="title-subsection"
                      marginBottom={3}
                      data-testid="highlightsSectionTitle"
                    >
                      {highlightsSectionTitle}
                    </Typography>
                    <BulletPointsDescription text={highlights} />
                  </Column>
                ) : null}
              </Row>
              {item.linkUrl && (
                <Row marginTop={6}>
                  <Column span={12}>
                    <Link
                      data-testid="viewDetailsLinkFooter"
                      marginBottom={{ xs: 3 }}
                      href={item.linkUrl}
                      fontSkin="body-standard"
                      onClick={() => {
                        trackNavigationClicks({
                          categoryId: merchandisingCategoryId,
                          pageZone: 'stockOptions',
                          destinationUrl: item.linkUrl,
                          tabIndex: tabIndex + 1,
                          stockOptionsCtaText: 'View full details'
                        })
                      }}
                    >
                      {viewDetailsCta}
                    </Link>
                  </Column>
                </Row>
              )}
              {!item.isOutOfStock && (
                <Row>
                  <Column span={12}>
                    <Typography textColor="subtle">
                      <PriceDisplay
                        data-testid="priceDisplay"
                        mpvId={mpvId}
                        quantity={item.selectedProductVariants.quantity}
                        selections={item.selectedProductVariants.variants}
                      />
                    </Typography>
                    <CouponCallout
                      mpvId={mpvId}
                      data-testid="couponCallout"
                      trackingData={{ pageZone: 'Stock Options' }}
                    />
                  </Column>
                </Row>
              )}
              <StockOptionCTA
                browseDesign={browseDesign}
                browseDesignCta={browseDesignCta}
                uploadDesignCta={uploadDesignCta}
                productName={item.name}
                productId={item.productId}
                mpvId={mpvId}
                version={item.version}
                quantity={item.selectedProductVariants.quantity}
                tabIndex={tabIndex}
                disableUploadFlow={isUploadFlowDisabled(mpvId)}
                isOutOfStock={item.isOutOfStock}
              />
            </Column>
          </Row>
        </TabContent>
      </>
    )
  )
}

export default StockOptionContent
